import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded'
import {
  ArrowElbowDownLeft,
  ClockCounterClockwise,
  MagnifyingGlass,
  SquaresFour
} from '@phosphor-icons/react'
// Futuro: habilitar la campana cuando se implemente el sistema de notificaciones.
// import NotificationsBell from './topbar/NotificationsBell'
import UserSessionMenu from './topbar/UserSessionMenu'
import { useAuth } from '../auth/AuthContext'
import { NavIcon } from './navIcons'
import { filterNavSearchItems, getNavSearchItems } from './navSearchItems'

const RECENT_MODULES_STORAGE_KEY = 'topbar-recent-navigation-modules'
const MAX_RECENT_MODULES = 4
const HOME_MODULE_URL = '/inicio'

const highlightModuleLabel = (label, query) => {
  const normalizedQuery = query.trim().toLowerCase()
  const index = label.toLowerCase().indexOf(normalizedQuery)
  if (!normalizedQuery || index < 0) return label

  return (
    <>
      {label.slice(0, index)}
      <mark>{label.slice(index, index + query.trim().length)}</mark>
      {label.slice(index + query.trim().length)}
    </>
  )
}

const commandMatchesPath = (command, pathname) =>
  command.url === pathname || command.activeUrls?.includes(pathname)

const getCommandSubtitle = (command, hasQuery) => {
  if (hasQuery)
    return [command.section, command.group].filter(Boolean).join(' · ') || 'Módulo del menú'
  if (command.url === HOME_MODULE_URL) return 'Inicio'
  return 'Visitado recientemente'
}

function CommandPalette({
  commandInputRef,
  commandQuery,
  setCommandQuery,
  visibleCommands,
  activeCommandIndex,
  setActiveCommandIndex,
  executeCommand,
  closeCommandPalette,
  hasQuery,
  hasRecentModules,
  towerAppearance = true
}) {
  const captionLabel = hasQuery
    ? 'Módulos del menú'
    : hasRecentModules
      ? 'Visitados recientemente'
      : 'Inicio'

  return (
    <div
      className={`navigation-command-overlay${towerAppearance ? ' navigation-command-overlay--tower' : ''}`}
      role="presentation"
      onMouseDown={closeCommandPalette}
    >
      <section
        className="navigation-command-palette"
        id="topbar-command-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Buscar módulos del menú"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="navigation-command-search">
          <MagnifyingGlass size={22} weight="regular" />
          <label className="sr-only" htmlFor="topbar-module-search">
            Buscar módulos del menú
          </label>
          <input
            id="topbar-module-search"
            ref={commandInputRef}
            value={commandQuery}
            onChange={(event) => setCommandQuery(event.target.value)}
            placeholder="Buscar ..."
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded="true"
            aria-controls="topbar-command-results"
            aria-activedescendant={
              visibleCommands[activeCommandIndex]
                ? `topbar-command-option-${activeCommandIndex}`
                : undefined
            }
          />
          <button type="button" onClick={closeCommandPalette} aria-label="Cerrar buscador">
            <kbd>Esc</kbd>
          </button>
        </div>
        <div className="navigation-command-caption">
          <span>
            {hasQuery ? (
              <SquaresFour size={14} weight="regular" />
            ) : hasRecentModules ? (
              <ClockCounterClockwise size={14} weight="regular" />
            ) : (
              <NavIcon name="home" size={14} className="" />
            )}
            {captionLabel}
          </span>
          <span>
            {visibleCommands.length} módulo{visibleCommands.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="navigation-command-list" id="topbar-command-results" role="listbox">
          {visibleCommands.map((command, index) => (
            <button
              type="button"
              key={command.url}
              id={`topbar-command-option-${index}`}
              role="option"
              aria-selected={index === activeCommandIndex}
              className={index === activeCommandIndex ? 'is-active' : ''}
              onMouseEnter={() => setActiveCommandIndex(index)}
              onClick={() => executeCommand(command)}
            >
              <span className="navigation-command-module-icon" aria-hidden="true">
                <NavIcon name={command.icon} size={18} className="" />
              </span>
              <span className="navigation-command-copy">
                <strong>{highlightModuleLabel(command.label, commandQuery)}</strong>
                <small>{getCommandSubtitle(command, hasQuery)}</small>
              </span>
              {towerAppearance ? (
                <ArrowElbowDownLeft
                  className="navigation-command-open-icon"
                  size={16}
                  aria-hidden="true"
                />
              ) : null}
            </button>
          ))}
          {!visibleCommands.length ? <p>No encontramos ese módulo.</p> : null}
        </div>
      </section>
    </div>
  )
}

const getRecentModuleUrls = () => {
  try {
    const storedModules = JSON.parse(localStorage.getItem(RECENT_MODULES_STORAGE_KEY) || '[]')
    return Array.isArray(storedModules) ? storedModules : []
  } catch {
    return []
  }
}

const TopBar = ({
  collapsed = false,
  onToggleCollapse,
  isMobileLayout = false,
  onOpenMobileNav = () => {}
}) => {
  const { isBootstrapping } = useAuth()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const commandInputRef = useRef(null)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [commandQuery, setCommandQuery] = useState('')
  const [activeCommandIndex, setActiveCommandIndex] = useState(0)
  const [recentModuleUrls, setRecentModuleUrls] = useState(getRecentModuleUrls)
  const collapsedClass = collapsed ? 'collapsed' : ''
  const towerTopBarClass = 'top-bar--tower-users top-bar--tower-html'
  const tooltipText = collapsed ? 'Fijar menú abierto' : 'Contraer menú'

  const visibleNavigationOptions = useMemo(
    () =>
      getNavSearchItems().map((item) => ({
        ...item,
        url: item.path
      })),
    [isBootstrapping]
  )

  const homeModule = useMemo(
    () => visibleNavigationOptions.find((option) => option.url === HOME_MODULE_URL),
    [visibleNavigationOptions]
  )

  const recentModules = useMemo(
    () =>
      recentModuleUrls
        .map((url) => visibleNavigationOptions.find((option) => option.url === url))
        .filter((option) => option && option.url !== HOME_MODULE_URL)
        .slice(0, MAX_RECENT_MODULES),
    [recentModuleUrls, visibleNavigationOptions]
  )

  const visibleCommands = useMemo(() => {
    const query = commandQuery.trim()
    if (query) return filterNavSearchItems(visibleNavigationOptions, query)

    return [homeModule, ...recentModules].filter(Boolean)
  }, [commandQuery, homeModule, recentModules, visibleNavigationOptions])

  const closeCommandPalette = () => {
    setCommandPaletteOpen(false)
    setCommandQuery('')
    setActiveCommandIndex(0)
  }

  const openCommandPalette = () => {
    setCommandPaletteOpen(true)
    setCommandQuery('')
    setActiveCommandIndex(0)
  }

  const executeCommand = (command) => {
    if (command) navigate(command.url)
    closeCommandPalette()
  }

  const commandPalette = commandPaletteOpen ? (
    <CommandPalette
      commandInputRef={commandInputRef}
      commandQuery={commandQuery}
      setCommandQuery={setCommandQuery}
      visibleCommands={visibleCommands}
      activeCommandIndex={activeCommandIndex}
      setActiveCommandIndex={setActiveCommandIndex}
      executeCommand={executeCommand}
      closeCommandPalette={closeCommandPalette}
      hasQuery={Boolean(commandQuery.trim())}
      hasRecentModules={recentModules.length > 0}
    />
  ) : null

  useEffect(() => {
    const currentModule = visibleNavigationOptions.find((option) =>
      commandMatchesPath(option, pathname)
    )
    if (!currentModule || currentModule.url === HOME_MODULE_URL) return

    setRecentModuleUrls((currentUrls) => {
      const updatedUrls = [
        currentModule.url,
        ...currentUrls.filter((url) => url !== currentModule.url && url !== HOME_MODULE_URL)
      ].slice(0, MAX_RECENT_MODULES)

      try {
        localStorage.setItem(RECENT_MODULES_STORAGE_KEY, JSON.stringify(updatedUrls))
      } catch {
        // La navegación no debe depender de que localStorage esté disponible.
      }

      return updatedUrls
    })
  }, [pathname, visibleNavigationOptions])

  useEffect(() => {
    if (!commandPaletteOpen) return undefined

    const focusTimer = window.setTimeout(() => commandInputRef.current?.focus(), 0)
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeCommandPalette()
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveCommandIndex((index) =>
          Math.min(index + 1, Math.max(visibleCommands.length - 1, 0))
        )
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveCommandIndex((index) => Math.max(index - 1, 0))
      }
      if (event.key === 'Enter') {
        event.preventDefault()
        executeCommand(visibleCommands[activeCommandIndex])
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.clearTimeout(focusTimer)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeCommandIndex, commandPaletteOpen, visibleCommands])

  useEffect(() => {
    setActiveCommandIndex(0)
  }, [commandQuery])

  if (isMobileLayout) {
    const mobileTitle = pathname.startsWith('/productos')
      ? 'Planifica'
      : pathname.startsWith('/clientes')
        ? 'Planifica'
        : ''

    return (
      <>
        <div className={`top-bar top-bar--mobile ${collapsedClass} ${towerTopBarClass}`}>
          <div className="top-bar-left">
            <button
              type="button"
              className="top-bar-toggle"
              onClick={() => onOpenMobileNav()}
              title="Abrir menú de navegación"
              aria-label="Abrir menú de navegación"
              aria-expanded="false"
            >
              <MenuIcon className="top-bar-toggle-icon" />
            </button>
            <button
              type="button"
              className="top-bar-command-search"
              onClick={openCommandPalette}
              aria-label="Buscar módulos"
              aria-haspopup="dialog"
              aria-controls="topbar-command-dialog"
              aria-expanded={commandPaletteOpen}
              title="Buscar módulos"
            >
              <MagnifyingGlass size={15} weight="regular" />
              <span>Buscar...</span>
            </button>
          </div>
          {mobileTitle ? <div className="top-bar-mobile-title">{mobileTitle}</div> : null}
          <div className="top-bar-right">
            {/* Futuro: habilitar la campana cuando se implemente el sistema de notificaciones. */}
            {/* <NotificationsBell towerTypography /> */}
            <UserSessionMenu compact towerTypography />
          </div>
        </div>
        {commandPalette}
      </>
    )
  }

  return (
    <>
      <div className={`top-bar ${collapsedClass} ${towerTopBarClass}`}>
        <div className="top-bar-left">
          <button
            type="button"
            className={`top-bar-toggle ${collapsed ? 'is-collapsed' : ''}`}
            onClick={() => onToggleCollapse?.(!collapsed)}
            title={tooltipText}
            aria-label={tooltipText}
            aria-expanded={!collapsed}
          >
            <MenuOpenRoundedIcon className="top-bar-toggle-icon" />
          </button>
          <span className="top-bar-divider" aria-hidden="true" />
          <button
            type="button"
            className="top-bar-command-search"
            onClick={openCommandPalette}
            aria-label="Buscar módulos"
            aria-haspopup="dialog"
            aria-controls="topbar-command-dialog"
            aria-expanded={commandPaletteOpen}
            title="Buscar módulos"
          >
            <MagnifyingGlass size={16} weight="regular" />
            <span>Buscar ...</span>
          </button>
        </div>
        <div className="top-bar-right">
          {/* Futuro: habilitar la campana cuando se implemente el sistema de notificaciones. */}
          {/* <NotificationsBell towerTypography /> */}
          <UserSessionMenu towerTypography />
        </div>
      </div>
      {commandPalette}
    </>
  )
}

export default memo(TopBar)
